import { BALANCE } from '../constants';
import type { Simulation } from '../sim';
import { ORE_IRON } from '../types';
import { charterReady } from './town';
import { functionalCapacity } from './workforce';

export interface LetterDef {
  id: string;
  from: string;
  subject: string;
  body(sim: Simulation): string;
  trigger(sim: Simulation): boolean;
  onDeliver?(sim: Simulation): void;
  /** false = archive-only: delivered silently to Mail, never auto-opened
   * (for letters whose beat is already covered by a dedicated modal). */
  autoOpen?: false;
}

const $ = (cents: number): string => `$${Math.floor(cents / 100).toLocaleString('en-US')}`;

/** The tutorial arc. Checked in order, at most one delivery per day. */
export const LETTER_DEFS: LetterDef[] = [
  {
    id: 'welcome',
    from: 'Consolidated Extraction Co. HQ',
    subject: 'Welcome to the claim',
    body: () =>
      'The truck is gone and it will not be back for you.\n\n' +
      'The surveyors flagged iron east of the stake — you can see it glinting on the surface. ' +
      'Take the DIG tool (E), drag a rectangle over the ore, and your miners will do the rest. ' +
      'Haulers carry what they dig to the depot; when the depot fills, a truck sells it at market.\n\n' +
      'Do not embarrass us.',
    trigger: (sim) => sim.state.meta.tick > 10,
  },
  {
    id: 'first-ore',
    from: 'Foreman',
    subject: 'First ore out of the ground',
    body: () =>
      'Boys pulled the first ore today. Haulers are moving it to the depot.\n\n' +
      'The truck heads to market on its own once the depot holds about 20 tons, or you can send it ' +
      'early with SELL ORE up top. Prices drift week to week — sell smart.',
    trigger: (sim) => sim.state.piles.size > 0 || sim.state.economy.depot.ironDt + sim.state.economy.depot.goldDt > 0,
  },
  {
    id: 'payday-looms',
    from: 'Bookkeeper',
    subject: 'Payday is Monday',
    body: (sim) =>
      `Every Monday I settle the books: wages out (${$(BALANCE.tierWageCents[0])} a head for miners and ` +
      `haulers), rent and canteen takings back in, fuel for the generator. Right now that is ` +
      `${sim.state.peeps.filter((peep) => peep.job === 'miner' || peep.job === 'hauler').length} on ` +
      'payroll.\n\nIf the cash box runs dry, HQ will notice. You do not want HQ to notice.',
    trigger: (sim) => sim.day >= 5,
  },
  {
    id: 'first-sale',
    from: 'Consolidated Extraction Co. HQ',
    subject: 'First shipment received',
    body: () =>
      'Your first ore shipment cleared the market. Adequate.\n\n' +
      'We have opened hiring for your site: keep beds free and the Monday bus will bring applicants. ' +
      'More hands, more ore. House them, feed them, and mind the water tank.',
    trigger: (sim) => sim.firstSaleDone,
    onDeliver: (sim) => {
      sim.state.workforce.hiringOpen = true;
    },
  },
  {
    id: 'beds-full',
    from: 'Foreman',
    subject: 'No beds left',
    body: () =>
      'Every bunk on site is taken — the bus will stop bringing anyone new.\n\n' +
      'A trailer sleeps two. A bunkhouse sleeps eight but wants power. Workers also eat: a canteen ' +
      'turns wages back into company money and keeps the crew fed.',
    trigger: (sim) => {
      const beds = functionalCapacity(sim.state, 'beds');
      return sim.state.workforce.hiringOpen && sim.state.peeps.length >= beds;
    },
  },
  {
    id: 'water-low',
    from: 'Bookkeeper',
    subject: 'Water costs money',
    body: () =>
      `Tank ran low, so I ordered a delivery — ${$(BALANCE.waterRefillCents)} for ${BALANCE.waterRefillUnits} units, ` +
      'and it will keep happening.\n\n' +
      `An aquifer well (${$(BUILDING_WELL_COST)}) pays for itself: powered, piped to the tank, it pulls ` +
      'water out of the ground for free. The drillers say the aquifer under us is good.',
    trigger: (sim) => sim.state.water.refillArrivesOnDay !== null || sim.state.water.tankLevelUnits < 200,
  },
  {
    id: 'petition-saloon',
    from: 'The crew (petition, 12 signatures)',
    subject: 'A man needs a drink',
    body: () =>
      'We dig your ore. We sleep in your beds. We eat at your canteen.\n\n' +
      'There is NOTHING TO DO in this camp. Build a saloon or the next bus out will have our names on ' +
      'the manifest.\n\n(A saloon also takes their wages back over the bar. — Bookkeeper)',
    trigger: (sim) => sim.state.workforce.unlocked.drink && sim.state.workforce.needs.drink < 0.5,
  },
  {
    id: 'hq-quota-iron',
    from: 'Consolidated Extraction Co. HQ',
    subject: 'Production quota',
    body: () =>
      `The board expects ${BALANCE.quotaIronDt / 10} tons of iron sold by week ${BALANCE.quotaByWeek}. ` +
      `Meet it and there is a ${$(BALANCE.quotaRewardCents)} bonus — and we will send the casino ` +
      'blueprints your grubby little boomtown keeps asking about.\n\nMiss it and we revisit your budget.',
    trigger: (sim) => sim.week >= 3,
    onDeliver: (sim) => {
      sim.state.letters.quota = {
        oreType: ORE_IRON,
        amountDt: BALANCE.quotaIronDt,
        deliveredDt: 0,
        byWeek: BALANCE.quotaByWeek,
        rewardCents: BALANCE.quotaRewardCents,
        completed: false,
        failed: false,
      };
    },
  },
  {
    id: 'gold-fever',
    from: 'Foreman',
    subject: 'GOLD',
    body: () =>
      'One of the deep cuts hit a gold vein. The men are talking.\n\n' +
      'Gold prices swing hard — check the market before you sell. Sometimes buyers pay half again the ' +
      'usual rate when the fever is on. Sometimes the bottom falls out.',
    trigger: (sim) => {
      if (sim.state.economy.depot.goldDt > 0) return true;
      for (const pile of sim.state.piles.values()) if (pile.goldDt > 0) return true;
      for (const peep of sim.state.peeps) if (peep.carryType === 2 && peep.carryDt > 0) return true;
      return false;
    },
  },
  {
    id: 'casino-unlocked',
    from: 'Consolidated Extraction Co. HQ',
    subject: 'Quota met — casino approved',
    body: () =>
      `Quota met. The bonus is in the cash box, and the casino blueprints are attached.\n\n` +
      'A word of advice from the board: casinos print money and trouble in equal measure. ' +
      "You may want a sheriff before the first Saturday night.",
    trigger: (sim) => sim.state.letters.quota?.completed === true,
  },
  {
    id: 'trouble-brewing',
    from: 'Sheriffless concerned citizens',
    subject: 'This camp is getting rowdy',
    body: () =>
      'Brawls. Theft. Half the shift hungover.\n\n' +
      "The bigger and drunker this camp gets, the worse it will be. A sheriff's office cuts trouble to " +
      'a third. Just saying.',
    trigger: (sim) => sim.state.incidents.log.length >= 2 && !sim.state.buildings.some((b) => b.type === 'sheriff'),
  },
  {
    id: 'first-veteran',
    from: 'Foreman',
    subject: 'The old hands',
    body: () =>
      'Some of the crew have stopped being drifters and started being miners. The men call them ' +
      'veterans — they dig faster, they steady the young ones, and they cost more, because they are ' +
      'worth more.\n\n' +
      'Fair warning: a veteran starts thinking about roots. Wife, kids, a porch. If this camp ever ' +
      'wants to be more than tents and dust, that is how it starts.',
    trigger: (sim) => sim.firstVeteranSeen,
  },
  {
    id: 'roots-talk',
    from: 'Foreman',
    subject: 'Talk around the cookfire',
    body: () =>
      'The veterans talk at night. Families, they say. A real street. But no one brings children to a ' +
      'camp without a SCHOOL and a DOCTOR.\n\n' +
      'Build both, put up a cottage or two, and I reckon the first letters home get written that same week.',
    trigger: (sim) =>
      sim.firstVeteranSeen &&
      !sim.state.buildings.some((b) => (b.type === 'school' || b.type === 'hospital') && b.functional),
  },
  {
    id: 'hq-nursery',
    from: 'Consolidated Extraction Co. HQ',
    subject: 'RE: Line item — "SCHOOL"',
    body: () =>
      'Our auditors flagged a structure on your site ledger described as a SCHOOL.\n\n' +
      'You are running a mine, not a nursery. The board expects ore, not report cards. We will be ' +
      'watching your shipment volumes with renewed interest.\n\n' +
      '(Unofficially: my father built the school in Copper Flats. Towns outlive mines. Spend wisely. — R.)',
    trigger: (sim) => sim.state.buildings.some((b) => b.type === 'school'),
  },
  {
    id: 'first-family',
    from: 'Foreman',
    subject: 'The first family',
    body: () =>
      'A wagon came in today with furniture on it. Furniture! Curtains went up in a window by sundown.\n\n' +
      "The men found reasons to walk past that cottage all evening. Don't tell HQ, but this stopped " +
      'being a camp today.\n\n' +
      'Families pay rent and shop at the store — real money that does not come out of the ground. Worth remembering.',
    trigger: (sim) => sim.state.households.length >= 1,
  },
  {
    id: 'town-stirring',
    from: 'Bookkeeper',
    subject: 'This is becoming a town',
    body: (sim) =>
      `Three families on the books now, ${sim.state.peeps.length} souls in total. Laundry lines between ` +
      'the cottages. A school bell in the morning.\n\n' +
      'The ledger says the town itself earned more this week than a full ore truck. Keep the families ' +
      'happy — clean air, services, safety — and this place could pay for itself long after the ore is gone.',
    trigger: (sim) => sim.state.households.length >= 3,
  },
  {
    id: 'second-generation',
    from: 'Foreman',
    subject: 'The second generation',
    body: () =>
      'One of the town kids signed on at the mine this morning. Grew up on our street, learned figures ' +
      'at our school, and chose to stay.\n\n' +
      'That is the whole game, boss. A town that raises its own people does not die when a vein does.',
    trigger: (sim) => sim.kidsGrownUp >= 1,
  },
  {
    id: 'hq-quota-2',
    from: 'Consolidated Extraction Co. HQ',
    subject: 'Revised production quota',
    body: () =>
      `The board notes your... civic enthusiasm. It also notes that ore pays for it. New quota: ` +
      `${(4_000 / 10).toFixed(0)} tons of iron sold by week 18. Bonus ${$(150_000)}.\n\n` +
      'Deliver, and the board will tolerate your little town. Fall short, and we will revisit what ' +
      'this site is FOR.',
    trigger: (sim) =>
      sim.week >= 10 && (sim.state.letters.quota === null || sim.state.letters.quota.completed || sim.state.letters.quota.failed),
    onDeliver: (sim) => {
      sim.state.letters.quota = {
        oreType: ORE_IRON,
        amountDt: 4_000,
        deliveredDt: 0,
        byWeek: 18,
        rewardCents: 150_000,
        completed: false,
        failed: false,
      };
    },
  },
  {
    id: 'charter-offer',
    from: 'Territorial Land Office',
    subject: 'Your settlement qualifies for incorporation',
    body: () =>
      'Surveyor records show families in residence, a school, physician services, and a house of ' +
      'community at your settlement. Under territorial law, it may incorporate as a TOWN — with a ' +
      'name of record, the right to levy taxes, and standing to attract residents.\n\n' +
      'The charter awaits signature at your convenience. Towns get names. Camps get forgotten.',
    trigger: (sim) => charterReady(sim),
  },
  {
    id: 'incorporated-gazette',
    from: 'The Territorial Gazette — Editor',
    subject: 'Welcome to the map',
    body: (sim) =>
      `The Gazette notes with satisfaction the incorporation of ${sim.state.meta.townName ?? 'a new town'}. ` +
      'We have added it to the masthead map, between two towns that no longer exist.\n\n' +
      'Our advice, offered free: towns live on what they build, not what they dig. The Gazette keeps a ' +
      'file on every camp in the territory. Most files end the same way. Surprise us.',
    trigger: (sim) => sim.state.meta.townName !== null,
  },
  {
    id: 'rail-opened',
    from: 'Territorial & Pacific Railroad',
    subject: 'Scheduled service commences',
    body: (sim) =>
      `The Territorial & Pacific is pleased to announce scheduled freight and passenger service to ` +
      `${sim.state.meta.townName ?? 'your town'}.\n\n` +
      'Your ore now travels at carload rates (better prices at the depot), and every train brings ' +
      'strangers with money. Keep the town worth stepping off for.',
    trigger: (sim) => sim.state.buildings.some((b) => b.type === 'railDepot' && b.functional),
  },
  {
    id: 'first-resident',
    from: 'Bookkeeper',
    subject: 'New names on the rolls',
    body: () =>
      'A family arrived this week with no letter from any foreman — no miner among them at all. They ' +
      'chose this town because it is a town.\n\n' +
      'Mark the date. That is the first dollar this settlement earned by existing.',
    trigger: (sim) => sim.state.households.some((household) => household.workerId === null),
  },
  {
    id: 'university-open',
    from: 'Faculty of the Desert University',
    subject: 'Term begins',
    body: () =>
      'The University opens its doors — an improbable dome in an improbable town. The children of ' +
      'miners will read Homer within sight of the pit their parents dug.\n\n' +
      'Graduates enter the workforce as skilled Regulars. Some may even stay.',
    trigger: (sim) => sim.state.buildings.some((b) => b.type === 'university' && b.functional),
  },
  {
    id: 'hq-pullout-letter',
    from: 'Consolidated Extraction Co. — Office of the Board',
    subject: 'NOTICE OF SITE CLOSURE',
    body: () =>
      'The ore is finished, and so is our interest. This site will be closed and its assets ' +
      'liquidated.\n\nTwo courtesies are extended to site management — a final settlement, or ' +
      'purchase of the entire site on weekly terms. The ultimatum on your desk has the figures.\n\n' +
      'Choose quickly. The board does not extend courtesies twice.',
    trigger: (sim) => sim.state.meta.era !== 'company',
    autoOpen: false, // the pull-out ultimatum modal already covers this beat
  },
  {
    id: 'independence-day',
    from: 'The Territorial Gazette — Editor',
    subject: 'A town buys itself',
    body: (sim) =>
      `${sim.state.meta.townName ?? 'The town'} has completed purchase of its own ground from the ` +
      'Consolidated Extraction Company. The Gazette has printed many closure notices. This is our ' +
      'first independence notice.\n\n' +
      'What remains is arithmetic: can a town in the desert pay its own way? Twelve good weeks would ' +
      'answer it. The territory is watching.',
    trigger: (sim) => sim.state.meta.era === 'independent' && sim.state.meta.ending === null,
  },
  {
    id: 'endure-halfway',
    from: 'Bookkeeper',
    subject: 'The books balance',
    body: (sim) =>
      `Six straight weeks in the black without leaning on the pit. Taxes, rail, commerce — ` +
      `${sim.state.meta.townName ?? 'this town'} is carrying its own weight.\n\n` +
      'Six more and even the Gazette will have to admit it. Keep the people happy and the trains met.',
    trigger: (sim) => sim.state.town.endureStreak >= 6 && sim.state.meta.ending === null,
  },
  {
    id: 'ghost-warning',
    from: 'The Territorial Gazette — Editor',
    subject: 'A file grows thicker',
    body: () =>
      'Our stringer reports empty windows on your main street and a bus that leaves fuller than it ' +
      'arrives.\n\nWe keep a file on every town in the territory. Most files end the same way. Yours ' +
      'has begun to look familiar. Prove us wrong — fill the beds, balance the books, keep the ' +
      'lights on.',
    trigger: (sim) =>
      sim.state.meta.era !== 'company' &&
      sim.state.meta.ending === null &&
      (sim.state.town.insolventStreak >= 3 || sim.state.peeps.length < 12),
  },
  {
    id: 'bailout',
    from: 'Consolidated Extraction Co. HQ — Legal',
    subject: 'RE: Your empty cash box',
    body: () =>
      `We have wired ${$(BALANCE.bailoutCents)} to keep your site solvent. This is a LOAN at ` +
      `${Math.round(BALANCE.debtInterestPerWeek * 100)}% weekly interest, not a gift.\n\n` +
      'There will not be a second one. If the site goes under again, we foreclose and your name goes ' +
      'on the industry blacklist.',
    trigger: (sim) => sim.state.economy.bailoutsUsed >= 1,
  },
  // ---- Stage 5: the post-ore economy. Append-only — delivered letter ids
  // live in saves, and defs are checked in order. ----
  {
    id: 'reclamation-plans',
    from: 'Territorial Land Office',
    subject: 'On the matter of your holes',
    body: () =>
      'An incorporated town is entitled to heal its own ground. The RECLAIM tool will fill any ' +
      'excavation back to grade — priced by the depth of the scar.\n\n' +
      'Filled land takes a plow as well as virgin desert, and families breathe easier without dust ' +
      'blowing off an open pit. The territory has seen mining camps die with the ore. The ones that ' +
      'live plant something in the hole.',
    trigger: (sim) => sim.state.meta.townName !== null,
  },
  {
    id: 'first-harvest',
    from: 'Foreman',
    subject: 'Something growing out there',
    body: () =>
      'Never thought I would see it: crops coming up on the claim. The hands are stacking the ' +
      'harvest at the field edge and the haulers are moving it like it was ore.\n\n' +
      'It sells like ore too — build a FREIGHT OFFICE and the wagon will run goods to market. Or a ' +
      'MARKET HALL, and the town buys its own.',
    trigger: (sim) => sim.state.goodsPiles.size > 0,
  },
  {
    id: 'feed-lot',
    from: 'Foreman',
    subject: 'The cattle eat first',
    body: () =>
      'Ranch is up. Fair warning from the boys who know livestock: cattle eat corn, every day, no ' +
      'exceptions. Keep a corn farm producing and the haulers will keep the feed bunk full.\n\n' +
      'An empty bunk means no cattle worth selling. The herd does not negotiate.',
    trigger: (sim) => sim.state.buildings.some((building) => building.type === 'cattleRanch'),
  },
  {
    id: 'freight-open',
    from: 'Territorial & Western Railroad — Freight Desk',
    subject: 'Account opened',
    body: () =>
      'Your freight office is on our books. The wagon departs when the depot holds enough value to ' +
      'cover the fee, or on your say-so.\n\n' +
      'Prices drift week to week, same as ore. A warehouse holds more between runs; a rail depot ' +
      'gets you the through-freight rate on everything.',
    trigger: (sim) => sim.state.buildings.some((building) => building.type === 'freightOffice' && building.functional),
  },
  {
    id: 'first-goods-sale',
    from: 'The Territorial Gazette — Editor',
    subject: 'A town that makes something',
    body: (sim) =>
      `${sim.state.meta.townName ?? 'Your camp'} shipped goods to market this week — grown and made, ` +
      'not dug. We noted it on page one.\n\n' +
      'Ore runs out. Every camp in the territory knows it and almost none act on it. Our file on ' +
      'your town just got a new column: the one where we keep the survivors.',
    trigger: (sim) => sim.firstGoodsSaleDone,
  },
  {
    id: 'university-modernity',
    from: 'The University — Office of the Dean',
    subject: 'What the graduates are building',
    body: (sim) =>
      `The first cohort out of the ${sim.state.meta.townName ?? 'town'} university did not go looking ` +
      'for a seam. They came back with drawings: glass drawn into wafers, silicon etched into ' +
      'switches, panels that drink the desert sun, and a reactor that never sleeps.\n\n' +
      'The blueprints are yours: the SEMICONDUCTOR FAB, SOLAR ARRAY, and NUCLEAR PLANT are ready to ' +
      'build. The century turned while nobody in the territory was watching. It turned here first.',
    trigger: (sim) =>
      sim.state.meta.townName !== null &&
      sim.state.buildings.some((building) => building.type === 'university' && building.functional),
  },
  {
    id: 'grid-hums',
    from: 'Territorial Power Authority',
    subject: 'Interconnection agreement',
    body: () =>
      'Your interconnect is on our grid. Any surplus your networks generate sells to the territory ' +
      'at the going rate, settled weekly with the books.\n\n' +
      'The rate drifts like any other commodity. Towns that light other towns tend to outlive them.',
    trigger: (sim) => sim.powerSellableSurplus > 0,
  },
  {
    id: 'atom-age',
    from: 'The Territorial Gazette — Editor',
    subject: 'The atom comes to the desert',
    body: (sim) =>
      `A reactor, in ${sim.state.meta.townName ?? 'a town'} that started as six tents and a hole. ` +
      'We ran the photograph across four columns.\n\n' +
      'Keep its crew paid and its cooling water flowing. The Gazette prints safety records too.',
    trigger: (sim) => sim.state.buildings.some((building) => building.type === 'nuclearPlant'),
  },
  {
    id: 'first-scram',
    from: 'Nuclear Plant — Shift Supervisor',
    subject: 'RE: the sirens',
    body: () =>
      'The reactor tripped itself offline — a scram, by the book, nobody hurt. The checks take a ' +
      'few days and then she comes back on her own.\n\n' +
      'Anything drawing on that network browns out until then. A diesel generator or a solar field ' +
      'on the same wires keeps the lights on through the dark days.',
    trigger: (sim) => sim.state.incidents.log.some((entry) => entry.kind === 'scram'),
  },
  {
    id: 'first-boom',
    from: 'Freight Desk — Market Wire',
    subject: 'Prices are moving',
    body: (sim) => {
      const event = sim.state.economy.marketEvent;
      const name = event ? event.good : 'a commodity';
      return (
        `The wire says ${name} is commanding a premium — buyers are paying well over the mean and ` +
        'will be for a week or two before it settles.\n\n' +
        'When a boom hits something you can ship, ship it. The wagon fee is the same either way.'
      );
    },
    trigger: (sim) => sim.state.economy.marketEvent?.kind === 'boom',
  },
  {
    id: 'first-bust',
    from: 'Freight Desk — Market Wire',
    subject: 'Bottom fell out',
    body: (sim) => {
      const event = sim.state.economy.marketEvent;
      const name = event ? event.good : 'a commodity';
      return (
        `The wire says the ${name} market cratered. It will find its way back to the mean in a week ` +
        'or two — they always do.\n\n' +
        'A warehouse means never selling into a bust. Hold the stock, pay the hands, wait it out.'
      );
    },
    trigger: (sim) => sim.state.economy.marketEvent?.kind === 'bust',
  },
];

const BUILDING_WELL_COST = 120_000;
